import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import FaceOutlinedIcon from "@mui/icons-material/FaceOutlined";
import DragIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectSequenceVariables } from "../../../app/slices/TrainingSlice";
import { emotionBubbleStylesMapped } from "../../../models/interactions/EmotionBubble";
import { InteractionType } from "../../../models/interactions/Interaction";
import { IMessageBubble } from "../../../models/interactions/MessageBubble";
import RichTextEditor from "../../rich_text/RichTextEditor";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import { useAnimationLabel } from "../animation/PiAnimationPicker";
import { ConversationLine, useConversationCast } from "./ConversationLines.hook";
import { hasLookatTarget, isNoLookat } from "../../../models/NoLookat";

/**
 * One line of a conversation, chat style (Design Studio
 * `screens/lookat-rework/cv2c-chat-closed.html`): avatar on the side with a
 * settings cog and a delete button under it (one click each, instead of the
 * design's "…" menu), and a read-only summary ("Lisa → Player · Talking")
 * above an editable bubble. Player lines sit on the right.
 */
export function ConversationChatLine(props: {
  line: ConversationLine;
  conversationId: string;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { line, isSelected } = props;
  const { t } = useTranslation();
  const { nameOf, avatarOf, isProp } = useConversationCast(props.conversationId);
  const speakerIsProp = isProp(line.npcId);
  const animationLabel = useAnimationLabel(line.animation);
  const isPlayer = line.npcId === "player";
  const isEmotion = line.type === InteractionType.EmotionBubble;
  const targetName = isNoLookat(line.lookAt)
    ? String(t("chip_select.no_actor"))
    : line.lookAt
      ? nameOf(line.lookAt)
      : "";

  const { isDragging, attributes, listeners, transform, transition, setNodeRef } = useSortable({
    id: line.id,
  });

  const actionClass =
    "flex items-center justify-center w-[28px] h-[28px] p-0 rounded-full border-none bg-transparent cursor-pointer transition-colors outline-none focus-visible:bg-interaction-primary-washed";
  const actions = (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={props.onDelete}
        aria-label={String(t("delete"))}
        title={String(t("delete"))}
        className={classNames(actionClass, "text-interaction-primary-faded hover:text-creator-error")}
        data-testid="conversation-line-delete"
      >
        <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
      </button>
    </div>
  );

  const meta = (
    <div
      // Always one line (Design Studio cv3-chat-*): names keep their width, the head look is an
      // icon with a tooltip, and the animation name truncates.
      className={classNames(
        "flex items-center gap-1.5 min-w-0 max-w-full min-h-[20px] whitespace-nowrap text-interaction-primary",
        { "justify-end": isPlayer }
      )}
      data-testid="conversation-line-meta"
    >
      {/* Quieter than the bubble it sits on (Design Studio bubbles-current › Summary): the
          speaker carries the weight, the listener is regular, look and animation are small and muted. */}
      <span className="flex-none text-[14px] font-semibold">{nameOf(line.npcId)}</span>
      {targetName && (
        <>
          <ArrowForwardIcon className="flex-none text-creator-text-soft" sx={{ fontSize: 14 }} />
          <span className="flex-none text-[14px] font-normal">{targetName}</span>
        </>
      )}
      {/* Whole body is the default and stays implicit; only the head look is called out. */}
      {hasLookatTarget(line.lookAt) && line.finalIK && (
        <>
          <span className="w-[3px] h-[3px] mx-1 flex-none rounded-full bg-creator-text-soft" />
          <span
            className="flex flex-none text-creator-text-sub"
            title={String(t("look_mode.head_only"))}
            aria-label={String(t("look_mode.head_only"))}
            role="img"
            data-testid="conversation-line-look"
          >
            <FaceOutlinedIcon sx={{ fontSize: 14 }} />
          </span>
        </>
      )}
      {/* Props can't play animations, so a prop line has no animation to show (older
          lines may still carry the default Talk_1 from before that rule). */}
      {!speakerIsProp && (
        <>
          <span className="w-[3px] h-[3px] mx-1 flex-none rounded-full bg-creator-text-soft" />
          <span
            className="inline-flex items-center gap-1 min-w-0 text-[12px] font-normal text-creator-text-sub"
            title={animationLabel}
            data-testid="conversation-line-animation"
          >
            <DirectionsWalkOutlinedIcon className="flex-none" sx={{ fontSize: 14 }} />
            <span className="min-w-0 truncate">{animationLabel}</span>
          </span>
        </>
      )}
    </div>
  );

  const bubble = isEmotion ? (
    <div
      className={classNames(
        "flex items-center justify-center w-[76px] h-[76px] border border-solid shadow-[0_6px_18px_rgba(21,52,88,0.08)]",
        {
          "self-end rounded-[10px] rounded-tr-none bg-interaction-primary border-interaction-primary": isPlayer,
          "rounded-[10px] rounded-tl-none bg-creator-surface border-interaction-primary-washed": !isPlayer,
        }
      )}
    >
      <img
        className="max-w-[40px] max-h-[40px]"
        src={emotionBubbleStylesMapped.find((e) => e.name === line.messageBubbleStyle)?.path}
        alt={line.messageBubbleStyle}
      />
    </div>
  ) : (
    <LineText line={line as IMessageBubble} isPlayer={isPlayer} />
  );

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      tabIndex={-1}
      style={{ transform: CSS.Translate.toString(transform), transition: transition || undefined }}
      // Selection is a full-width tinted band. Padding is
      // the same either way, so opening a line's settings never moves it.
      // Clicking the line opens its settings (the panel's × closes them).
      onClick={() => {
        if (!isSelected) props.onSelect();
      }}
      className={classNames("group relative flex px-3 py-2 rounded-lg transition-colors cursor-pointer outline-none focus:outline-none focus-visible:outline-none", {
        "justify-end": isPlayer,
        "opacity-70": isDragging,
        "bg-interaction-primary-selected dark:bg-[#2f4250]":
          isSelected,
      })}
      data-testid="conversation-line"
      data-selected={isSelected}
    >
      <div
        // Up to 80% of the chat, never wider than 640px, fitted to its text: lines keep
        // their shape when the settings panel narrows the chat (Design Studio cv3-chat-*).
        className={classNames("flex gap-2 min-w-0 max-w-[min(80%,640px)]", {
          "flex-row-reverse": isPlayer,
        })}
      >
        <div className="flex flex-col items-center gap-1">
          {avatarOf(line.npcId)}
          <div onClick={(event) => event.stopPropagation()}>{actions}</div>
        </div>
        <div className={classNames("flex flex-col min-w-0 gap-2 mt-3", { "items-end": isPlayer })}>
          {meta}
          {bubble}
        </div>
      </div>
      <DragIcon
        className={classNames(
          "absolute top-1 opacity-0 transition-opacity text-interaction-primary-faded group-hover:opacity-100",
          { "left-0": isPlayer, "right-0": !isPlayer }
        )}
      />
    </div>
  );
}

function LineText(props: { line: IMessageBubble; isPlayer: boolean }) {
  const { line, isPlayer } = props;
  const { getValue, changeValue } = useTrainingLocalization();
  const variables = useSelector(selectSequenceVariables);
  const value = getValue(line.text);

  return (
    <div
      className={classNames(
        "flex items-center w-fit max-w-full min-w-[120px] min-h-[52px] px-4 py-3 box-border border border-solid shadow-[0_6px_18px_rgba(21,52,88,0.08)] cursor-text",
        {
          "rounded-[10px] rounded-tr-none bg-interaction-primary border-interaction-primary": isPlayer,
          "rounded-[10px] rounded-tl-none bg-creator-surface border-interaction-primary-washed": !isPlayer,
        }
      )}
      // Typing in the bubble shouldn't open the settings panel or start a drag.
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <RichTextEditor
        ariaLabel="conversation-message-text"
        value={value}
        onCommit={(next) => changeValue(line.text, next)}
        variables={variables}
        debounceMs={250}
        flushOnCleanupKeys={[line.id, value]}
        className="relative w-full"
        contentClassName={classNames("w-full text-16 font-normal leading-6", {
          "text-interaction-text-inverted caret-white": isPlayer,
          "text-interaction-text-primary caret-interaction-primary": !isPlayer,
        })}
        suggestionsMaxWidth={420}
        warningClassName="mt-[6px] text-[12px] font-medium text-creator-error"
      />
    </div>
  );
}
